<div class="w-full mx-auto p-5 space-y-6">

  <!-- Header -->
  <div class="flex flex-wrap justify-between items-center gap-4">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">Configuración de Tasas de Cambio</h1>
      <p class="text-gray-500 mt-1">Administra la configuración del servicio de Exchange Rate API</p>
    </div>
    <div class="flex items-center gap-4">
      <!-- Estado del Servicio -->
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-gray-700">Estado:</span>
        <span [ngClass]="getStatusColor()" class="font-semibold">
          {{ getStatusText() }}
        </span>
        <div class="w-3 h-3 rounded-full" [ngClass]="serviceStatus ? 'bg-green-500' : 'bg-red-500'"></div>
      </div>
      <!-- Botón Actualizar Tasas -->
      <button 
        (click)="updateRates()"
        [disabled]="!serviceStatus || isLoading"
        class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Actualizar Tasas
      </button>
    </div>
  </div>

  <!-- Estado y Estadísticas -->
  <div *ngIf="currentConfig" class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
    <h3 class="text-lg font-semibold text-gray-800 mb-4">Estado del Servicio</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      
      <!-- Estado del Servicio -->
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Estado</p>
            <p [ngClass]="getStatusColor()" class="text-xl font-bold">
              {{ getStatusText() }}
            </p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" 
               [ngClass]="serviceStatus ? 'bg-green-100' : 'bg-red-100'">
            <svg class="w-6 h-6" [ngClass]="serviceStatus ? 'text-green-600' : 'text-red-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path *ngIf="serviceStatus" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              <path *ngIf="!serviceStatus" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Última Actualización de Tasas -->
      <div class="bg-blue-50 p-4 rounded-lg">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Última Actualización de Tasas</p>
            <p class="text-lg font-semibold text-blue-900">
              {{ formatDate(currentConfig.lastRatesUpdate) }}
            </p>
          </div>
          <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Intervalo de Actualización -->
      <div class="bg-yellow-50 p-4 rounded-lg">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-600">Intervalo de Actualización</p>
            <p class="text-lg font-semibold text-yellow-900">
              {{ currentConfig.updateIntervalHours }} horas
            </p>
          </div>
          <div class="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Formulario de Configuración -->
  <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
    <h3 class="text-lg font-semibold text-gray-800 mb-6">Configuración del Servicio</h3>
    
    <form [formGroup]="configForm" (ngSubmit)="onSubmit()">
      
      <!-- Estado del Servicio -->
      <div class="mb-6">
        <div class="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <label class="text-base font-medium text-gray-700">
              Habilitar Servicio de Exchange Rate
            </label>
            <p class="text-sm text-gray-500 mt-1">
              Activar o desactivar la actualización automática de tasas de cambio
            </p>
          </div>
          <label class="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              class="sr-only peer" 
              formControlName="isEnabled">
            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      <!-- URL Base de la API -->
      <div class="mb-6">
        <label for="baseUrl" class="block text-gray-700 font-bold mb-2" appRequiredLabel>
          URL Base de la API
        </label>
        <input
          type="url"
          id="baseUrl"
          formControlName="baseUrl"
          class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          [ngClass]="{'border-red-500': hasError('baseUrl', 'required') || hasError('baseUrl', 'maxlength')}"
          placeholder="https://api.currencybeacon.com/v1"
        >
        <div class="text-red-500 text-sm mt-1" *ngIf="hasError('baseUrl', 'required')">
          La URL base es obligatoria
        </div>
        <div class="text-red-500 text-sm mt-1" *ngIf="hasError('baseUrl', 'maxlength')">
          La URL no puede exceder 500 caracteres
        </div>
        <p class="text-xs text-gray-500 mt-1">
          URL de la API externa para obtener tasas de cambio
        </p>
      </div>

      <!-- API Key -->
      <div class="mb-6">
        <label for="apiKey" class="block text-gray-700 font-bold mb-2" appRequiredLabel>
          Clave de API
        </label>
        <div class="relative">
          <input
            type="password"
            id="apiKey"
            formControlName="apiKey"
            class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
            [ngClass]="{'border-red-500': hasError('apiKey', 'required') || hasError('apiKey', 'maxlength')}"
            placeholder="Ingrese su API Key"
          >
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
        </div>
        <div class="text-red-500 text-sm mt-1" *ngIf="hasError('apiKey', 'required')">
          La clave de API es obligatoria
        </div>
        <div class="text-red-500 text-sm mt-1" *ngIf="hasError('apiKey', 'maxlength')">
          La clave de API no puede exceder 200 caracteres
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Clave de autenticación para acceder a la API de tasas de cambio
        </p>
      </div>

      <!-- Intervalo de Actualización -->
      <div class="mb-6">
        <label for="updateIntervalHours" class="block text-gray-700 font-bold mb-2" appRequiredLabel>
          Intervalo de Actualización (horas)
        </label>
        <div class="relative">
          <input
            type="number"
            id="updateIntervalHours"
            formControlName="updateIntervalHours"
            min="1"
            max="168"
            class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            [ngClass]="{'border-red-500': hasError('updateIntervalHours', 'required') || hasError('updateIntervalHours', 'min') || hasError('updateIntervalHours', 'max')}"
          >
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <span class="text-gray-500 sm:text-sm">horas</span>
          </div>
        </div>
        <div class="text-red-500 text-sm mt-1" *ngIf="hasError('updateIntervalHours', 'required')">
          El intervalo de actualización es obligatorio
        </div>
        <div class="text-red-500 text-sm mt-1" *ngIf="hasError('updateIntervalHours', 'min') || hasError('updateIntervalHours', 'max')">
          El intervalo debe estar entre 1 y 168 horas (1 semana)
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Frecuencia con la que se actualizarán automáticamente las tasas de cambio
        </p>
      </div>

      <!-- Información de Actualización -->
      <div *ngIf="currentConfig" class="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 class="font-medium text-gray-800 mb-2">Información de Actualización</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span class="text-gray-600">Última actualización de configuración:</span>
            <p class="font-medium text-gray-800">{{ formatDate(currentConfig.lastConfigUpdate) }}</p>
          </div>
          <div>
            <span class="text-gray-600">Última actualización de tasas:</span>
            <p class="font-medium text-gray-800">{{ formatDate(currentConfig.lastRatesUpdate) }}</p>
          </div>
        </div>
      </div>

      <!-- Botones -->
      <div class="flex justify-end gap-4">
        <button
          type="button"
          (click)="loadConfiguration()"
          [disabled]="isLoading || submitting"
          class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancelar
        </button>
        <button
          type="submit"
          [disabled]="!isFormValid || submitting"
          class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span *ngIf="!submitting">Guardar Configuración</span>
          <span *ngIf="submitting" class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Guardando...
          </span>
        </button>
      </div>
    </form>
  </div>

  <!-- Ayuda y Documentación -->
  <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
    <div class="flex">
      <div class="flex-shrink-0">
        <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-medium text-blue-800">Información del Servicio</h3>
        <div class="mt-2 text-sm text-blue-700">
          <ul class="list-disc list-inside space-y-1">
            <li>El servicio utiliza Currency Beacon API para obtener tasas de cambio en tiempo real</li>
            <li>Las tasas se actualizan automáticamente según el intervalo configurado</li>
            <li>La moneda base del sistema es COP (Peso Colombiano)</li>
            <li>Se recomienda un intervalo de actualización de 4 horas para uso normal</li>
            <li>Deshabilitar el servicio afectará todas las conversiones de moneda en el sistema</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

</div>
